import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Linking } from 'react-native';
import { LogOut } from 'lucide-react-native';
import { router } from 'expo-router';
import Constants from 'expo-constants';
import { useTheme, SPACING, BORDER_RADIUS, FONT_SIZES, TYPOGRAPHY, withWeight } from '../../theme';
import { useUIStore } from '../../store/uiStore';
import { applyUpdate } from '@utils/serviceWorker';

const FOOTER_HIT_SLOP = { top: 8, bottom: 8, left: 4, right: 4 };

interface ProfileFooterProps {
  onSignOut: () => void;
  isLoading: boolean;
}

// Real build identifiers baked in by app.config.ts (extra.buildSha /
// extra.builtAt) so `Redi v1.0.0` in the footer means something — Jason can
// confirm exactly which build a screenshot or bug report is on. Falls back
// to just the version if the build wasn't produced through app.config.ts
// (e.g. some non-standard tooling context).
function buildInfoLine(version: string): string {
  const extra = Constants.expoConfig?.extra as { buildSha?: string; builtAt?: string } | undefined;
  const sha = extra?.buildSha;
  const builtAt = extra?.builtAt;
  if (!sha || !builtAt) return `Redi v${version}`;
  const formatted = new Date(builtAt).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return `Redi v${version} · ${sha} · ${formatted}`;
}

export function ProfileFooter({ onSignOut, isLoading }: ProfileFooterProps) {
  const { colors } = useTheme();
  const swUpdateAvailable = useUIStore((s) => s.swUpdateAvailable);
  const version = Constants.expoConfig?.version ?? '1.0.0';

  return (
    <>
      {swUpdateAvailable && (
        <TouchableOpacity
          style={[styles.updatePill, { borderColor: colors.borderStrong }]}
          onPress={applyUpdate}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Update available. Tap to reload.">
          <Text style={[styles.updatePillText, { color: colors.text }]}>
            Update available · Tap to reload
          </Text>
        </TouchableOpacity>
      )}

      {/* Bordered danger pill, not a pink block: in Design B `danger` is a
          hairline border plus risk-colored label (Button's danger variant). */}
      <TouchableOpacity
        style={[styles.signOutButton, { borderColor: colors.dangerBorder }]}
        onPress={onSignOut}
        disabled={isLoading}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel="Sign Out">
        <LogOut size={18} color={colors.danger} />
        <Text style={[styles.signOutText, { color: colors.danger }]}>Sign Out</Text>
      </TouchableOpacity>

      <View style={styles.footer}>
        <View style={styles.footerLinks}>
          <TouchableOpacity
            onPress={() => router.push('/profile/faq' as never)}
            hitSlop={FOOTER_HIT_SLOP}>
            <Text style={[styles.footerLink, { color: colors.textTertiary }]}>FAQ</Text>
          </TouchableOpacity>
          <Text style={[styles.footerDot, { color: colors.textTertiary }]}>·</Text>
          <TouchableOpacity
            onPress={() => Linking.openURL('mailto:support@weareredi.net')}
            hitSlop={FOOTER_HIT_SLOP}>
            <Text style={[styles.footerLink, { color: colors.textTertiary }]}>Help</Text>
          </TouchableOpacity>
          <Text style={[styles.footerDot, { color: colors.textTertiary }]}>·</Text>
          <TouchableOpacity onPress={() => router.push('/profile/terms')} hitSlop={FOOTER_HIT_SLOP}>
            <Text style={[styles.footerLink, { color: colors.textTertiary }]}>Terms</Text>
          </TouchableOpacity>
          <Text style={[styles.footerDot, { color: colors.textTertiary }]}>·</Text>
          <TouchableOpacity
            onPress={() => router.push('/profile/privacy')}
            hitSlop={FOOTER_HIT_SLOP}>
            <Text style={[styles.footerLink, { color: colors.textTertiary }]}>Privacy</Text>
          </TouchableOpacity>
        </View>
        <Text
          style={[styles.version, { color: colors.textTertiary }]}
          // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
          // 'wide': the box width varies per build (sha + built-at glyphs),
          // so the audit overpaints past the box edges.
          dataSet={{ visualmask: 'wide' }}>
          {buildInfoLine(version)}
        </Text>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  // Bordered pill, not a green wash: the update notice is an action, and
  // green here reads as a readiness signal.
  updatePill: {
    alignSelf: 'center',
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.xs,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: StyleSheet.hairlineWidth,
    marginBottom: SPACING.md,
  },
  updatePillText: {
    ...withWeight(TYPOGRAPHY.bodySmall, 'semibold'),
  },
  signOutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACING.md,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: StyleSheet.hairlineWidth,
    marginBottom: SPACING.lg,
  },
  signOutText: {
    ...TYPOGRAPHY.button,
    fontSize: FONT_SIZES.md,
    marginLeft: SPACING.sm,
  },
  footer: {
    alignItems: 'center',
  },
  footerLinks: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: SPACING.xs,
  },
  footerLink: {
    ...TYPOGRAPHY.bodySmall,
    paddingHorizontal: SPACING.xs,
    paddingVertical: SPACING.xs,
  },
  footerDot: {
    ...TYPOGRAPHY.bodySmall,
    paddingHorizontal: SPACING.xxs,
  },
  version: {
    ...TYPOGRAPHY.bodySmall,
    textAlign: 'center',
  },
});
